import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import {
  fetchOutrightsAllEvents,
  selectEventById,
  selectOutrightsLoading,
  selectOutrightsError,
  selectAllEvents
} from 'stores/outrights';
import MarketGroup from './partials/MarketGroup';
import { marketCategories, daysLeft, sortMarketsByPriority, eventDisplayName } from './utils';
import { OUTRIGHT_ACCENT, OUTRIGHT_ACCENT_RGB } from './theme';

function OutrightDetail() {
  const { eventId: rawEventId } = useParams();
  const eventId = decodeURIComponent(rawEventId || '');
  const navigate = useNavigate();
  const { t } = useTranslation('translations', { useSuspense: false });
  const dispatch = useDispatch();

  const event = useSelector((state) => selectEventById(state, eventId));
  const allEvents = useSelector(selectAllEvents);
  const loading = useSelector(selectOutrightsLoading);
  const error = useSelector(selectOutrightsError);

  const [searchParams] = useSearchParams();
  const initialMarket = searchParams.get('market') || 'all';
  const [activeCategory, setActiveCategory] = useState(initialMarket);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (allEvents.length === 0) {
      dispatch(fetchOutrightsAllEvents());
    }
  }, [dispatch, allEvents.length]);

  const categories = useMemo(() => marketCategories(event, t), [event, t]);
  const visibleMarkets = useMemo(() => {
    if (!event) return [];
    if (activeCategory === 'all') return sortMarketsByPriority(event.markets);
    return event.markets.filter((m) => m.stringId === activeCategory);
  }, [event, activeCategory]);

  if (loading && !event) {
    return (
      <Container fluid className="g-0">
        <div className="px-3 py-3 text-center" style={{ color: 'var(--bs-body-color)' }}>
          {t('outrights.loading')}
        </div>
      </Container>
    );
  }

  if (!loading && !event) {
    return (
      <Container fluid className="g-0">
        <div className="px-3 py-4 text-center" style={{ color: 'var(--bs-body-color)' }}>
          <div className="mb-2">{error || t('outrights.notFound')}</div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => navigate('/outrights')}
            style={{ background: OUTRIGHT_ACCENT, color: '#fff', border: 'none', fontWeight: 600 }}
          >
            {t('outrights.backToList')}
          </button>
        </div>
      </Container>
    );
  }

  if (!event) return null;
  const se = event.se;
  const displayName = eventDisplayName(event, t);
  const dl = daysLeft(se.schedule_StartTime);
  const dlLabel =
    dl == null
      ? ''
      : dl < 0
        ? t('outrights.inProgress')
        : t('outrights.daysLeft', { count: dl });

  return (
    <Container fluid className="g-0">
      <div
        className="px-3 py-3"
        style={{
          background:
            `linear-gradient(135deg, rgba(${OUTRIGHT_ACCENT_RGB}, 0.28), transparent)`,
          borderBottom: '1px solid var(--bs-border-color)'
        }}
      >
        <div
          className="d-flex align-items-center"
          style={{
            gap: 6,
            fontSize: '0.65rem',
            letterSpacing: '0.14em',
            opacity: 0.85,
            fontWeight: 700,
            color: 'var(--bs-body-color)',
            lineHeight: 1
          }}
        >
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label={t('outrights.back')}
            className="btn p-0 d-inline-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--bs-body-color)',
              width: 18,
              height: 18,
              marginRight: 2
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path
                d="M10 3L5 8l5 5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <span>{t('outrights.label').toUpperCase()}</span>
          <span style={{ opacity: 0.5 }}>/</span>
          <span style={{ color: OUTRIGHT_ACCENT }}>
            {displayName?.toUpperCase()}
          </span>
        </div>
        <div className="d-flex align-items-end gap-2 mt-1">
          <h1
            className="m-0 fw-bold flex-grow-1"
            style={{
              fontSize: '1.5rem',
              letterSpacing: '-0.03em',
              color: 'var(--bs-body-color)',
              lineHeight: 1
            }}
          >
            {displayName}
          </h1>
          <div className="d-flex flex-column align-items-end">
            <span
              style={{
                fontSize: '0.65rem',
                letterSpacing: '0.14em',
                opacity: 0.65,
                fontWeight: 700,
                color: 'var(--bs-body-color)'
              }}
            >
              {dl == null || dl < 0 ? '' : t('outrights.startsIn').toUpperCase()}
            </span>
            <span
              className="fw-bold"
              style={{
                fontSize: '1.1rem',
                color: OUTRIGHT_ACCENT,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1
              }}
            >
              {dlLabel}
            </span>
          </div>
        </div>
      </div>

      {categories.length > 0 && (
        <div
          className="d-flex gap-2 overflow-auto px-3 py-2"
          style={{
            scrollbarWidth: 'none',
            borderBottom: '1px solid var(--bs-border-color)'
          }}
        >
          <button
            type="button"
            onClick={() => setActiveCategory('all')}
            className="btn btn-sm rounded-pill flex-shrink-0"
            style={{
              background: activeCategory === 'all' ? OUTRIGHT_ACCENT : 'var(--bs-body-bg)',
              color: activeCategory === 'all' ? '#fff' : 'var(--bs-body-color)',
              border: activeCategory === 'all' ? 'none' : '1px solid var(--bs-border-color)',
              fontWeight: 700,
              whiteSpace: 'nowrap'
            }}
          >
            {t('outrights.allCategories')}
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setActiveCategory(c.id)}
              className="btn btn-sm rounded-pill flex-shrink-0"
              style={{
                background: activeCategory === c.id ? OUTRIGHT_ACCENT : 'var(--bs-body-bg)',
                color: activeCategory === c.id ? '#fff' : 'var(--bs-body-color)',
                border: activeCategory === c.id ? 'none' : '1px solid var(--bs-border-color)',
                fontWeight: 700,
                whiteSpace: 'nowrap'
              }}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      <div className="px-3 py-3">
        {visibleMarkets.length === 0 && (
          <div style={{ opacity: 0.65, color: 'var(--bs-body-color)' }}>
            {t('outrights.noMarkets')}
          </div>
        )}
        {visibleMarkets.map((market, i) => (
          <MarketGroup
            key={market.stringId || `${market.id}-${i}`}
            event={event}
            market={market}
            defaultExpanded={i === 0}
          />
        ))}
      </div>
    </Container>
  );
}

export default OutrightDetail;
